import {
    ApiShopperLandingPageLink,
    isApiFullLandingPageLink,
} from '@storefronts/common-types'
import { useContext, useEffect, useMemo } from 'react'

import { CartContext } from '../context/cartContext'
import { LandingPageLinksContext } from '../context/LandingPageLinksContext'
import { ViddyLPContext } from '../context/viddyLPContext'
import { collectCartUpsellCandidates } from '../utils/cartUpsellTools'

/**
 * Resolves the cart's co-purchase upsells into renderable landing page links. Candidates are derived
 * from the upsells the backend attached to each in-cart variant's anchor link, ordered by rank.
 * Hashes not yet cached are fetched (and resolve reactively once the link cache updates); only
 * links with an available variant are returned. Returns an empty list unless this storefront
 * (landing page) has dynamic cart upsells enabled.
 */
export const useDynamicCartUpsells = (): ApiShopperLandingPageLink[] => {
    const { cart } = useContext(CartContext)
    const { landingPage } = useContext(ViddyLPContext)
    const dynamicCartUpsellsEnabled = landingPage.dynamicCartUpsellsEnabled
    const {
        getCachedOrderedLinksForHashes,
        fetchAndCacheLinksForHashes,
        getLinkForProduct,
    } = useContext(LandingPageLinksContext)

    // Enrich each cart item's embedded link with the cached full link when available.
    // Recovered or stub lines may have a link object without cartUpsellVariants; the full
    // cached version (fetched by CartUpsells) carries that data.
    const enrichedCartItems = useMemo(
        () =>
            cart.items.map((item) => {
                const embeddedLink = item.variant.link
                if (!embeddedLink?.shopifyId) return item
                const cached = getLinkForProduct(embeddedLink.shopifyId)
                if (!cached || !isApiFullLandingPageLink(cached)) return item
                if (!cached.cartUpsellVariants) return item
                return {
                    ...item,
                    variant: {
                        ...item.variant,
                        link: {
                            ...embeddedLink,
                            cartUpsellVariants: cached.cartUpsellVariants,
                        },
                    },
                }
            }),
        [cart.items, getLinkForProduct],
    )

    const orderedHashes = useMemo(
        () =>
            dynamicCartUpsellsEnabled
                ? collectCartUpsellCandidates(enrichedCartItems).map(
                      (c) => c.linkHash,
                  )
                : [],
        [dynamicCartUpsellsEnabled, enrichedCartItems],
    )

    useEffect(() => {
        if (orderedHashes.length === 0) return
        fetchAndCacheLinksForHashes(orderedHashes)
    }, [orderedHashes, fetchAndCacheLinksForHashes])

    return useMemo(() => {
        if (orderedHashes.length === 0) return []
        const { orderedLinks } = getCachedOrderedLinksForHashes(orderedHashes)
        return orderedLinks.filter((link) =>
            link.variantInfo?.some((variant) => variant.available !== false),
        )
    }, [orderedHashes, getCachedOrderedLinksForHashes])
}
